import { useState, useEffect, useRef } from 'react';
import { supabase, authService } from '../lib/supabase';

// UUID fallback for non-HTTPS contexts where crypto.randomUUID() isn't available
function generateUUID(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

interface OnlineMember {
    name: string;
    online_at: string;
}

// Real visitor counter using Supabase Realtime Presence
// Tracks: total visits (persistent), active users (live), members online, saved decks
export function VisitCounter() {
    const [totalVisits, setTotalVisits] = useState<number>(0);
    const [hereNow, setHereNow] = useState<number>(1);
    const [membersHere, setMembersHere] = useState<OnlineMember[]>([]);
    const [memberCount, setMemberCount] = useState<number>(0);
    const [deckCount, setDeckCount] = useState<number>(0);
    const [error, setError] = useState(false);
    const [showMemberList, setShowMemberList] = useState(false);
    const memberListRef = useRef<HTMLDivElement>(null);
    const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

    useEffect(() => {
        // Generate or retrieve session ID
        const sessionId = sessionStorage.getItem('delta-session-id') || generateUUID();
        sessionStorage.setItem('delta-session-id', sessionId);

        // Create a presence channel for live tracking
        const channel = supabase.channel('presence-lobby', {
            config: {
                presence: {
                    key: sessionId,
                },
            },
        });
        channelRef.current = channel;

        // Track when presence state changes (someone joins/leaves)
        channel.on('presence', { event: 'sync' }, () => {
            const state = channel.presenceState();
            const keys = Object.keys(state);
            setHereNow(keys.length);

            // Extract logged-in members from presence state
            const members: OnlineMember[] = [];
            for (const key of keys) {
                const presences = state[key] as any[];
                for (const p of presences) {
                    if (p.display_name) {
                        members.push({
                            name: p.display_name,
                            online_at: p.online_at || '',
                        });
                    }
                }
            }
            // Deduplicate by name
            const unique = Array.from(new Map(members.map(m => [m.name, m])).values());
            setMembersHere(unique);
        });

        // Get current user info for presence
        const trackPresence = async () => {
            const user = await authService.getCurrentUser();
            const displayName = user?.user_metadata?.display_name || null;

            channel.subscribe(async (status) => {
                if (status === 'SUBSCRIBED') {
                    await channel.track({
                        online_at: new Date().toISOString(),
                        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 50) : 'unknown',
                        ...(displayName ? { display_name: displayName } : {}),
                    });
                }
            });

            // Re-track when auth state changes (login/logout)
            const { data: { subscription } } = authService.onAuthStateChange(async (newUser) => {
                const newName = newUser?.user_metadata?.display_name || null;
                if (channelRef.current) {
                    await channelRef.current.track({
                        online_at: new Date().toISOString(),
                        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 50) : 'unknown',
                        ...(newName ? { display_name: newName } : {}),
                    });
                }
            });

            return subscription;
        };

        let authSub: any;
        trackPresence().then(sub => { authSub = sub; });

        // Fetch stats
        const fetchStats = async () => {
            try {
                // Increment total visits (once per session)
                const hasVisited = sessionStorage.getItem('delta-visited');

                if (!hasVisited) {
                    sessionStorage.setItem('delta-visited', 'true');

                    // Increment visit count in Supabase
                    const { data: currentStats, error: fetchError } = await supabase
                        .from('stats')
                        .select('value')
                        .eq('key', 'total_visits')
                        .single();

                    if (fetchError) {
                        // Only create row if it truly doesn't exist (PGRST116 = row not found)
                        if (fetchError.code === 'PGRST116') {
                            console.log('Stats row not found, creating initial entry');
                            const cached = localStorage.getItem('delta-total-visits');
                            const cachedNum = cached ? parseInt(cached, 10) : 0;
                            const startValue = Math.max(cachedNum + 1, 1);
                            await supabase.from('stats').insert({ key: 'total_visits', value: startValue, updated_at: new Date().toISOString() });
                            setTotalVisits(startValue);
                            localStorage.setItem('delta-total-visits', String(startValue));
                        } else {
                            console.warn('Stats fetch error (not resetting):', fetchError.message);
                            const cached = localStorage.getItem('delta-total-visits');
                            setTotalVisits(cached ? parseInt(cached, 10) : 0);
                        }
                    } else {
                        const newCount = (currentStats?.value || 0) + 1;
                        const cached = localStorage.getItem('delta-total-visits');
                        const cachedNum = cached ? parseInt(cached, 10) : 0;
                        const safeCount = Math.max(newCount, cachedNum + 1);
                        await supabase.from('stats').update({ value: safeCount, updated_at: new Date().toISOString() }).eq('key', 'total_visits');
                        setTotalVisits(safeCount);
                        localStorage.setItem('delta-total-visits', String(safeCount));
                    }
                } else {
                    const { data, error: readError } = await supabase.from('stats').select('value').eq('key', 'total_visits').single();
                    if (!readError && data) {
                        setTotalVisits(data.value || 0);
                        localStorage.setItem('delta-total-visits', String(data.value || 0));
                    } else {
                        const cached = localStorage.getItem('delta-total-visits');
                        setTotalVisits(cached ? parseInt(cached, 10) : 0);
                    }
                }

                // Get member count from stats
                const { data: memberData, error: memberError } = await supabase
                    .from('stats')
                    .select('value')
                    .eq('key', 'member_count')
                    .single();
                if (!memberError && memberData) {
                    setMemberCount(memberData.value || 0);
                    localStorage.setItem('delta-member-count', String(memberData.value || 0));
                } else {
                    const cached = localStorage.getItem('delta-member-count');
                    setMemberCount(cached ? parseInt(cached, 10) : 0);
                }

                // Get deck count (count of public decks)
                const { count: decksCount, error: deckError } = await supabase
                    .from('decks')
                    .select('*', { count: 'exact', head: true })
                    .eq('is_public', true);
                if (!deckError && decksCount !== null) {
                    setDeckCount(decksCount);
                    localStorage.setItem('delta-deck-count', String(decksCount));
                } else {
                    const cached = localStorage.getItem('delta-deck-count');
                    setDeckCount(cached ? parseInt(cached, 10) : 0);
                }

            } catch (err) {
                console.error('Visit counter error:', err);
                setError(true);
                const cachedVisits = localStorage.getItem('delta-total-visits');
                const cachedMembers = localStorage.getItem('delta-member-count');
                const cachedDecks = localStorage.getItem('delta-deck-count');
                if (cachedVisits) setTotalVisits(parseInt(cachedVisits, 10));
                if (cachedMembers) setMemberCount(parseInt(cachedMembers, 10));
                if (cachedDecks) setDeckCount(parseInt(cachedDecks, 10));
            }
        };

        fetchStats();

        // Subscribe to realtime updates on stats table
        const statsChannel = supabase
            .channel('stats-changes')
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'stats' },
                (payload) => {
                    const updated = payload.new as { key: string; value: number };
                    if (updated.key === 'total_visits') {
                        setTotalVisits(updated.value);
                        localStorage.setItem('delta-total-visits', String(updated.value));
                    } else if (updated.key === 'member_count') {
                        setMemberCount(updated.value);
                        localStorage.setItem('delta-member-count', String(updated.value));
                    }
                }
            )
            .subscribe();

        // Close member list on outside click
        const handleClickOutside = (e: MouseEvent) => {
            if (memberListRef.current && !memberListRef.current.contains(e.target as Node)) {
                setShowMemberList(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        // Cleanup on unmount
        return () => {
            channel.unsubscribe();
            statsChannel.unsubscribe();
            authSub?.unsubscribe();
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const guestCount = hereNow - membersHere.length;

    return (
        <div className="flex flex-col items-center gap-0.5" title={error ? 'Connection issue' : 'Live stats'}>
            <div className="text-white/30 text-xs flex items-center gap-1.5">
                <span className="text-amber-400/50">⟡</span>
                <span>this plane has been walked {totalVisits.toLocaleString()} time{totalVisits !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-3 text-white/20 text-[10px]">
                <span className="flex items-center gap-1">
                    <span className="text-green-400/60 animate-pulse">●</span>
                    {hereNow} here now
                </span>
                {/* Members currently online (from presence) */}
                {membersHere.length > 0 && (
                    <div className="relative" ref={memberListRef}>
                        <button
                            onClick={() => setShowMemberList(!showMemberList)}
                            className="flex items-center gap-1 hover:text-cyan-400/80 transition-colors cursor-pointer"
                            title="Click to see who's online"
                        >
                            <span className="text-cyan-400/60">👤</span>
                            <span>{membersHere.length} member{membersHere.length !== 1 ? 's' : ''} here</span>
                        </button>
                        {showMemberList && (
                            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-44 bg-slate-900/95 border border-cyan-500/20 rounded-lg shadow-xl shadow-cyan-500/5 z-50 overflow-hidden backdrop-blur-sm">
                                <div className="px-2.5 py-1.5 border-b border-white/5 text-[10px] text-cyan-400/70 font-semibold uppercase tracking-wider">
                                    Online Now
                                </div>
                                <div className="max-h-32 overflow-y-auto">
                                    {membersHere.map((m, i) => (
                                        <div
                                            key={i}
                                            className="flex items-center gap-2 px-2.5 py-1.5 text-[11px] text-white/60 hover:bg-white/5"
                                        >
                                            <span className="text-green-400/70 text-[8px]">●</span>
                                            <span className="truncate">{m.name}</span>
                                        </div>
                                    ))}
                                    {guestCount > 0 && (
                                        <div className="flex items-center gap-2 px-2.5 py-1.5 text-[11px] text-white/30 italic">
                                            <span className="text-white/20 text-[8px]">●</span>
                                            <span>+{guestCount} guest{guestCount !== 1 ? 's' : ''}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {memberCount > 0 && (
                    <span className="flex items-center gap-1">
                        <span className="text-cyan-400/60">⟡</span>
                        {memberCount} members
                    </span>
                )}
                {deckCount > 0 && (
                    <span className="flex items-center gap-1">
                        <span className="text-purple-400/60">📚</span>
                        {deckCount} decks shared
                    </span>
                )}
            </div>
        </div>
    );
}
